'use client';

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/context/AuthContext';
import { queryKeys } from '@/lib/queries/queryKeys';
import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry';
import type { ForYouItem, ForYouResponse } from '@/app/api/me/for-you/route';

export type { ForYouItem };

const KNOWN_TYPES = new Set<string>(['details_needed']);

export async function fetchForYou(): Promise<ForYouItem[]> {
  const response = await fetchWithAuthRetry('/api/me/for-you');
  if (!response.ok) throw new Error(`for-you ${response.status}`);
  const body = (await response.json()) as ForYouResponse;
  // A newer server may send types this client cannot show yet.
  return (Array.isArray(body?.items) ? body.items : []).filter(item => KNOWN_TYPES.has(item?.type));
}

/**
 * The signed-in user's "For you" items. Nothing is fetched while signed out;
 * an error leaves `data` undefined, which every caller reads as "show nothing".
 * Under queryKeys.details.all, so a details save refreshes it.
 */
export function useForYou() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  return useQuery({
    queryKey: queryKeys.details.forUser(userId ?? ''),
    queryFn: fetchForYou,
    enabled: !!userId,
  });
}
